import { BRAND, pageTitle } from '@palaccount/config';
import { Check, Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { useEffectiveSettings, useOverrideSetting } from '@/features/admin/api';
import {
  APPEARANCE_DEFAULTS,
  appearanceFromSettings,
  applyAppearance,
  type Appearance,
  type AppearanceKey,
} from '@/lib/personalization';
import { useUiStore } from '@/lib/ui-store';
import { cn } from '@/lib/utils';

/**
 * ADM-08 — PERSONALIZATION (§15.4).
 *
 * "Tailor PalAccount to your eyes. Changes apply live across the whole app."
 *
 * ── NO SAVE, NO CANCEL, NO RESET ───────────────────────────────────────────
 *
 * §4: "Every control is a button. There is no Save and no Cancel. Tapping a
 * choice applies it to the whole system at once." BR-ADM-08-01 says the same
 * as a rule, and this file honours it in the strict sense: the choice is
 * applied to the document FIRST and written to the server afterwards, so a
 * slow network delays the record and never the effect.
 *
 * That ordering is also ERR-ADM-08-02's, which is the one error this screen
 * has: "That change was not saved to your account. It applies on this device
 * until you sign in again." A failed write is not rolled back — the person
 * asked for dark, they are looking at dark, and undoing it under them because
 * a request timed out would be the worse behaviour.
 *
 * ── WHAT IS DELIBERATELY ABSENT ────────────────────────────────────────────
 *
 * No language switch (§5: "Language is the EN / ع pair in the top bar"), no
 * view mode (owner ruling R1 removed it entirely), no number, date or currency
 * format (§5 puts those on ADM-01), no default landing page, and no Reset.
 * Every one of those absences is a sentence in §5.
 *
 * ── NO PERMISSION, AND THAT IS THE POINT ───────────────────────────────────
 *
 * §9 / BR-PERM-08: "EVERYBODY, whatever their role. Personalization has no
 * permission at all." The route carries no requirement and the write goes
 * through the settings API's `@SelfService` override path, which is the same
 * one ADM-01's personal rows use. A person holding no role at all — PART 16.1's
 * rung 4 — reaches this screen, which is exactly what BR-PERM-08 promises and
 * what made the rule "unsatisfiable" while the screen did not exist.
 */

interface OptionCardProps {
  settingKey: AppearanceKey;
  value: string;
  current: string;
  onChoose: (key: AppearanceKey, value: string) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  label: string;
  /**
   * The card this option belongs to. Prefixed onto the accessible name because
   * three of the six cards reuse a word — "Light" is a theme AND a font
   * weight, "Default" is a text size — and GEN-15's accessible name has to be
   * unambiguous on its own: a screen-reader user hearing "Light" out of
   * context learns nothing. The VISIBLE label and the tooltip stay the spec's
   * single word (§5 field 6: "The name shows only as a tooltip").
   */
  group: string;
}

/**
 * §4: "A chosen option carries a 1.5-pixel brand-blue border and a 3-pixel
 * soft blue glow; the others carry a plain grey border." Brand-BLUE is read as
 * the brand colour, which after the accent card may not be blue — the ring is
 * `--primary`, so choosing Teal recolours the selection ring too
 * (BR-ADM-08-09's "everywhere").
 */
function OptionButton({
  settingKey,
  value,
  current,
  onChoose,
  disabled,
  className,
  children,
  label,
  group,
}: OptionCardProps) {
  const chosen = current === value;
  return (
    <button
      type="button"
      onClick={() => onChoose(settingKey, value)}
      disabled={disabled === true}
      aria-pressed={chosen}
      aria-label={`${group}: ${label}`}
      title={label}
      className={cn(
        'relative flex items-center justify-center gap-2 rounded-lg border p-3 transition-all',
        chosen
          ? 'border-[1.5px] border-primary ring-[3px] ring-primary/25'
          : 'border-border hover:border-border/80',
        disabled === true && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {children}
      {chosen ? <Check className="absolute end-2 top-2 size-3.5 text-primary" aria-hidden /> : null}
    </button>
  );
}

export function PersonalizationPage() {
  const { t, i18n } = useTranslation();
  const { data: settings } = useEffectiveSettings();
  const override = useOverrideSetting();
  const setTheme = useUiStore((s) => s.setTheme);

  // Seeded from what is already on the document, so the cards are correct on
  // the first frame rather than flickering to the defaults and back.
  const [appearance, setAppearance] = useState<Appearance>(() => ({ ...APPEARANCE_DEFAULTS }));
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('personalize.title'));
  }, [t]);

  useEffect(() => {
    if (!settings) return;
    setAppearance(appearanceFromSettings(settings));
  }, [settings]);

  // BR-ADM-08-11 / ERR-ADM-08-01 / NFR-07 — "In Arabic the system must use the
  // Arabic typeface and must TELL THE READER that their font choice does not
  // apply" (NFR-07: "rather than letting a person pick a font that quietly
  // does nothing"). §6 asks for the card to be greyed out "with a reason
  // shown", and OQ-15-08 parks the alternative (a second set of Arabic faces)
  // as a decision.
  const arabic = i18n.language === 'ar';

  function choose(key: AppearanceKey, value: string): void {
    const next = { ...appearance, [key]: value };
    setAppearance(next);
    // APPLIED FIRST. BR-ADM-08-01: "the moment it is tapped, with no Save
    // button and no page reload."
    applyAppearance(next);
    // The top bar's own theme button reads the ui store, so the two controls
    // must not be able to disagree about what the page currently looks like.
    if (key === 'appearance.theme') setTheme(value === 'dark' ? 'dark' : 'light');

    setSaveFailed(false);
    override.mutate(
      { key, value },
      // ERR-ADM-08-02. Not rolled back — see the header.
      { onError: () => setSaveFailed(true) },
    );
  }

  return (
    // §4: "A single column, 760 pixels wide at most, centred."
    <div className="mx-auto grid w-full max-w-[760px] gap-4">
      <p className="text-sm text-muted-foreground">{t('personalize.intro', { brand: BRAND.name })}</p>

      {saveFailed ? (
        <div role="status" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
          {t('personalize.errNotSaved')}
        </div>
      ) : null}

      {/* ── Theme ── */}
      <SettingCard title={t('personalize.themeTitle')} note={t('personalize.themeNote')}>
        <div className="grid grid-cols-2 gap-3">
          {(['light', 'dark'] as const).map((mode) => (
            <OptionButton
              key={mode}
              settingKey="appearance.theme"
              value={mode}
              current={appearance['appearance.theme']}
              onChoose={choose}
              group={t('personalize.themeTitle')}
              label={t(`personalize.theme.${mode}`)}
              className="justify-start gap-3 py-4"
            >
              {/* §5 field 1 — "three colour squares as a preview", in the exact
                  colours the spec lists for each theme. */}
              <span className="flex gap-1" aria-hidden>
                {(mode === 'light'
                  ? ['#f6f7f9', '#2f63e0', '#ffffff']
                  : ['#0d1016', '#5b8def', '#161b24']
                ).map((hex) => (
                  <span
                    key={hex}
                    className="size-4 rounded-sm border border-black/10"
                    style={{ backgroundColor: hex }}
                  />
                ))}
              </span>
              {mode === 'light' ? (
                <Sun className="size-4" aria-hidden />
              ) : (
                <Moon className="size-4" aria-hidden />
              )}
              <span className="text-sm font-medium">{t(`personalize.theme.${mode}`)}</span>
            </OptionButton>
          ))}
        </div>
      </SettingCard>

      {/* ── Text size ── */}
      <SettingCard title={t('personalize.textSizeTitle')}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ['small', 13],
              ['default', 16],
              ['large', 19],
              ['xl', 23],
            ] as const
          ).map(([size, preview]) => (
            <OptionButton
              key={size}
              settingKey="appearance.text_size"
              value={size}
              current={appearance['appearance.text_size']}
              onChoose={choose}
              group={t('personalize.textSizeTitle')}
              label={t(`personalize.textSize.${size}`)}
              className="flex-col gap-1 py-4"
            >
              {/* §5 field 2 — "a large letter A at 13, 16, 19, and 23 pixels".
                  A fixed px preview on purpose: it is a SAMPLE of a size, and
                  a sample that resized with the setting would show nothing. */}
              <span style={{ fontSize: `${preview}px`, lineHeight: 1 }} aria-hidden>
                A
              </span>
              <span className="text-xs">{t(`personalize.textSize.${size}`)}</span>
            </OptionButton>
          ))}
        </div>
      </SettingCard>

      {/* ── Font style ── */}
      <SettingCard
        title={t('personalize.fontStyleTitle')}
        note={arabic ? t('personalize.errArabicFont') : t('personalize.fontStyleNote')}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ['jakarta', "'Plus Jakarta Sans Variable', system-ui"],
              ['plex', "'IBM Plex Sans Variable', system-ui"],
              ['source_serif', "'Source Serif 4 Variable', serif"],
              ['system', 'system-ui'],
            ] as const
          ).map(([font, stack]) => (
            <OptionButton
              key={font}
              settingKey="appearance.font_style"
              value={font}
              current={appearance['appearance.font_style']}
              onChoose={choose}
              // §6: "Should be greyed out in Arabic, with a reason shown."
              disabled={arabic}
              group={t('personalize.fontStyleTitle')}
              label={t(`personalize.fontStyle.${font}`)}
              className="flex-col gap-1 py-4"
            >
              <span style={{ fontFamily: stack, fontSize: '20px', lineHeight: 1 }} aria-hidden>
                Ag
              </span>
              <span className="text-xs">{t(`personalize.fontStyle.${font}`)}</span>
            </OptionButton>
          ))}
        </div>
      </SettingCard>

      {/* ── Font weight ── */}
      <SettingCard title={t('personalize.fontWeightTitle')} note={t('personalize.fontWeightNote')}>
        <div className="grid grid-cols-3 gap-3">
          {(
            [
              ['light', 300],
              ['regular', 400],
              ['medium', 500],
            ] as const
          ).map(([weight, css]) => (
            <OptionButton
              key={weight}
              settingKey="appearance.font_weight"
              value={weight}
              current={appearance['appearance.font_weight']}
              onChoose={choose}
              group={t('personalize.fontWeightTitle')}
              label={t(`personalize.fontWeight.${weight}`)}
              className="flex-col gap-1 py-4"
            >
              <span style={{ fontWeight: css, fontSize: '20px', lineHeight: 1 }} aria-hidden>
                Ag
              </span>
              <span className="text-xs">{t(`personalize.fontWeight.${weight}`)}</span>
            </OptionButton>
          ))}
        </div>
      </SettingCard>

      {/* ── Density ── */}
      <SettingCard title={t('personalize.densityTitle')} note={t('personalize.densityNote')}>
        <div className="grid grid-cols-3 gap-3">
          {(['compact', 'cozy', 'comfortable'] as const).map((density) => (
            <OptionButton
              key={density}
              settingKey="appearance.density"
              value={density}
              current={appearance['appearance.density']}
              onChoose={choose}
              group={t('personalize.densityTitle')}
              label={t(`personalize.density.${density}`)}
              className="py-3"
            >
              {/* §5 field 5 — "Text-only buttons, with no preview." */}
              <span className="text-sm">{t(`personalize.density.${density}`)}</span>
            </OptionButton>
          ))}
        </div>
      </SettingCard>

      {/* ── Accent colour ── */}
      <SettingCard title={t('personalize.accentTitle')} note={t('personalize.accentNote')}>
        <div className="flex flex-wrap gap-3">
          {/*
            These five hexes are a SECOND COPY of `index.css`'s
            `:root[data-accent='…'] { --accent-hue: … }` block. They must move
            together: a dot here that does not match the token there shows the
            reader a colour the product will not apply.

            THAT IS NOW ASSERTED, not just asked for. `src/test/theme-contrast.
            test.ts` reads both lists and compares them byte-for-byte and in
            order — the name matters as much as the hex, since the name is what
            `data-accent` selects on. Editing this array alone fails there.

            Teal, green and amber were darkened by delegated ruling D20
            (26 Aug 2026) so all five clear 4.5:1 under --primary-foreground;
            see the reasoning at the token block in `index.css`.
          */}
          {(
            [
              ['signal_blue', '#2f63e0'],
              ['indigo', '#5a4ee0'],
              ['teal', '#008087'],
              ['green', '#008545'],
              ['amber', '#a66300'],
            ] as const
          ).map(([accent, hex]) => (
            <OptionButton
              key={accent}
              settingKey="appearance.accent"
              value={accent}
              current={appearance['appearance.accent']}
              onChoose={choose}
              // §5 field 6: "The name shows only as a tooltip." A tooltip is
              // not an accessible name, so the swatch also carries an
              // aria-label (GEN-15) — colour is never the only signal.
              group={t('personalize.accentTitle')}
              label={t(`personalize.accent.${accent}`)}
              className="size-[46px] shrink-0 rounded-full p-0"
            >
              <span
                className="size-[30px] rounded-full"
                style={{ backgroundColor: hex }}
                aria-hidden
              />
            </OptionButton>
          ))}
        </div>
      </SettingCard>
    </div>
  );
}

/** §4's card: "a white rounded box with a 24-pixel inside margin". */
function SettingCard({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="grid gap-3 p-6">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold">{title}</h2>
          {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}
